import React, { useState, useEffect } from 'react';
import { SalesReport, SalesReportProductData, MetricType, LoadingData } from 'common/types';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from 'components/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { RiArrowLeftSFill, RiArrowRightSFill } from 'react-icons/ri';
import TableRow from '@mui/material/TableRow';
import { TableHead, IconButton, Button, Select, MenuItem, Box } from '@mui/material';
import classNames from 'classnames';
import moment, { Moment } from 'moment-timezone';
import Spinner from 'components/Spinner';
import SalesChart from 'components/SalesChart';
import { getPricingString } from 'common/utils/common';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import ShopLocationSelect from 'components/ShopLocationSelect';
import { useTranslation } from 'services/localization/useTranslation';
import useDebouncedValue from 'common/hooks/useDebouncedValue';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { useSelector } from 'react-redux';
import { Callable } from 'common/frontend/callable';
import { SalesReportByCurrency } from 'common/api/db/functions/_callable/reports';
import { uniq } from 'lodash';

const localeSpecFi: moment.LocaleSpecification = {
	months: 'tammikuu_helmikuu_maaliskuu_huhtikuu_toukokuu_kesäkuu_heinäkuu_elokuu_syyskuu_lokakuu_marraskuu_joulukuu'.split(
		'_',
	),
	monthsShort: 'tammi._helmi._maalis._huhti._touko._kesä._heinä._elo._syys._loka._marras._joulu.'.split(
		'_',
	),
	monthsParseExact: true,
	weekdays: 'sunnuntai_maanantai_tiistai_keskiviikko_torstai_perjantai_lauantai'.split('_'),
	weekdaysShort: 'su_ma_ti_ke_to_pe_la'.split('_'),
	weekdaysMin: 'su_ma_ti_ke_to_pe_la'.split('_'),
	weekdaysParseExact: true,
	longDateFormat: {
		LT: 'HH:mm',
		LTS: 'HH:mm:ss',
		L: 'DD/MM/YYYY',
		LL: 'D MMMM YYYY',
		LLL: 'D MMMM YYYY HH:mm',
		LLLL: 'dddd D MMMM YYYY HH:mm',
	},
	week: {
		dow: 1, // Monday is the first day of the week.
		doy: 4, // Used to determine first week of the year.
	},
};
const emptyReport: SalesReport = {
	revenue: 0,
	rentalsAmount: 0,
	shoppers: 0,
	topProducts: {},
};

const getSalesReport = (
	reportData: SalesReportByCurrency | null,
	options: { viewMode: 'month' | 'day'; currency: string; day: Moment },
) => {
	const { viewMode, currency, day } = options;
	const currencyReport = reportData?.[currency] ?? null;
	if (viewMode === 'month') return currencyReport?.total ?? emptyReport;
	const selectedDay = moment(day).date();
	return currencyReport?.daily[selectedDay]?.total ?? emptyReport;
};

const Sales = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const shop = useSelector(ShopSelectors.activeShopData)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const timezone = useSelector(ShopSelectors.getShopTimeZone)!;
	const lang = useSelector(ViewSelectors.lang);
	const [currentMonth, setCurrentMonth] = useState<Moment>(moment());
	const [currentDay, setCurrentDay] = useState<Moment>(moment());
	const [viewMode, setViewMode] = useState<'month' | 'day'>('month');
	const [reportData, setReportData] = useState<LoadingData<SalesReportByCurrency | null>>({
		loading: false,
		data: null,
		error: null,
	});
	const currencies = uniq([
		shop.publicInfo.currency.code,
		...(!reportData?.data ? [] : Object.keys(reportData.data)),
	]);
	const [selectedCurrency, setSelectedCurrency] = useState<string>(shop.publicInfo.currency.code);

	const activeLocation = useSelector(UserSelectors.userActiveLocation)!;
	const hasAllLocations = useSelector(UserSelectors.userHasAccessToAllLocations);

	const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
		hasAllLocations ? null : activeLocation.id,
	);
	const currencyObject = getCurrencyObjectFromCurrencyCode(
		selectedCurrency,
		shop.publicInfo.currency,
	);
	const debouncedMonth = useDebouncedValue(currentMonth, 1200);
	const currentReportData = getSalesReport(reportData?.data ?? null, {
		viewMode,
		currency: selectedCurrency,
		day: currentDay,
	});

	useEffect(() => {
		switch (lang) {
			case 'fi':
				moment.updateLocale('fi', localeSpecFi);
				break;
			case 'en':
				moment.locale('en');
				break;
			default:
				moment.locale('en');
				break;
		}
	}, [lang]);

	useEffect(() => {
		const start = moment(debouncedMonth).startOf('month').toISOString();
		const end = moment(debouncedMonth).endOf('month').toISOString();
		const fetchReportData = async (start: string, end: string) => {
			setReportData({ loading: true, data: null, error: null });
			try {
				const reportData = await Callable.reports.getSalesReport(
					{
						startDate: start,
						endDate: end,
						locationId: selectedLocationId,
						shopId,
						shopTimezone: timezone,
					},
					{ timeout: 300000 },
				);
				setReportData({ loading: false, data: reportData, error: null });
			} catch (e) {
				setReportData({ loading: false, data: null, error: t('common:errors.somethingWentWrong') });
			}
		};
		fetchReportData(start, end);
	}, [debouncedMonth, selectedLocationId, shopId, t, timezone]);

	const changeChartTimespan = (amount: number) => {
		if (viewMode === 'month') {
			const newDay = moment(currentDay).add(amount, 'months').startOf('month');
			setCurrentMonth(newDay);
			setCurrentDay(newDay);
		} else {
			const oldDay = currentDay;
			const newDay = moment(oldDay).add(amount, 'days');
			if (newDay.month() !== oldDay.month()) {
				setCurrentMonth(newDay);
			}
			setCurrentDay(newDay);
		}
	};

	const toggleViewMode = () => setViewMode(viewMode === 'month' ? 'day' : 'month');

	const renderMonthSelector = () => (
		<div className={classes.monthSelector}>
			<IconButton
				className={classes.button}
				aria-label={t('reports.sales.previousMonth', 'Previous month')}
				onClick={() => {
					changeChartTimespan(-1);
				}}
				disabled={reportData.loading}
				size="large"
			>
				<RiArrowLeftSFill />
			</IconButton>
			<Typography className={classes.dateHeader} variant="h4" color="primary">
				{viewMode === 'month' ? currentDay.format('MMMM - YYYY') : currentDay.format('ddd MMM Do')}
			</Typography>
			<IconButton
				className={classes.button}
				aria-label={t('reports.sales.nextMonth', 'Next month')}
				onClick={() => {
					changeChartTimespan(1);
				}}
				disabled={reportData.loading}
				size="large"
			>
				<RiArrowRightSFill />
			</IconButton>
		</div>
	);

	const renderMonthDaySwitch = () => (
		<div>
			<Button
				color="primary"
				onClick={() => toggleViewMode()}
				disabled={viewMode === 'month' || reportData.loading}
			>
				{t('common:times.month', 'month')}
			</Button>
			<Button
				color="primary"
				onClick={() => toggleViewMode()}
				disabled={viewMode === 'day' || reportData.loading}
			>
				{t('common:times.day', 'day')}
			</Button>
		</div>
	);

	const currencyOptions = currencies.map((currency) => (
		<MenuItem key={currency} value={currency}>
			{currency}
		</MenuItem>
	));

	const renderLocationSelection = () => (
		<ShopLocationSelect
			value={selectedLocationId ?? 'all'}
			onChange={(value) => setSelectedLocationId(value === 'all' ? null : value)}
		/>
	);

	const renderCurrencySelection = () => (
		<Select
			variant="standard"
			className={classes.select}
			classes={{
				select: classes.selectPadding,
			}}
			disableUnderline
			value={selectedCurrency}
			onChange={(e: any) => setSelectedCurrency(e.target.value)}
			inputProps={{
				name: 'currencySelection',
				id: 'currencySelection',
			}}
		>
			{currencyOptions}
		</Select>
	);

	const renderProductRow = (id: number, product: SalesReportProductData) => {
		const { name, count, revenue } = product;
		const content = getPricingString(revenue, currencyObject);
		return (
			<TableRow className={classes.lowHeight} key={id}>
				<TableCell className={classes.noBorder}>{name}</TableCell>
				<TableCell className={classes.noBorder} align="right">
					{count}
				</TableCell>
				<TableCell className={classNames(classes.noBorder, classes.priceCell)} align="right">
					{content}
				</TableCell>
			</TableRow>
		);
	};

	const reportToProductCardContent = (report: SalesReport) => {
		const rows = Object.values(report.topProducts)
			.sort((a, b) => b.revenue - a.revenue)
			.map((val, index) => renderProductRow(index, val));
		const { count, revenue } = Object.values(report.topProducts).reduce(
			(tot, product) => ({
				count: tot.count + product.count,
				revenue: tot.revenue + product.revenue,
			}),
			{ count: 0, revenue: 0 },
		);
		return (
			<Table className={classes.table} size="small">
				<TableHead>
					<TableRow>
						<TableCell className={classes.noBorder}>{t('reports.product', 'Product')}</TableCell>
						<TableCell className={classes.noBorder} align="right">
							{t('reports.productListCountColHeader', 'Amount')}
						</TableCell>
						<TableCell className={classes.noBorder} align="right">
							{t('reports.productListRevenueColHeader', 'Revenue')}
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows}
					<TableRow>
						<TableCell className={classes.noBorder}>
							<b>{t('common:payment.total')}</b>
						</TableCell>
						<TableCell className={classes.noBorder} align="right">
							<b>{count}</b>
						</TableCell>
						<TableCell className={classNames(classes.noBorder, classes.priceCell)} align="right">
							<b>{getPricingString(revenue, currencyObject)}</b>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		);
	};

	const reportToRevenueCardContent = (report: SalesReport) => (
		<Typography align="center" variant="h3" color="primary" data-qa="Reports--totalRevenue">
			{getPricingString(report.revenue, currencyObject)}
		</Typography>
	);

	const reportToRentalsCardContent = (report: SalesReport) => (
		<Typography align="center" variant="h3" color="primary">
			{report.rentalsAmount.toFixed(0)}
		</Typography>
	);

	const reportToShoppersCardContent = (report: SalesReport) => (
		<Typography align="center" variant="h3" color="primary">
			{report.shoppers.toFixed(0)}
		</Typography>
	);

	const renderMetricCard = (title: string, type: MetricType, report: SalesReport) => {
		let content;
		switch (type) {
			case 'revenue':
				content = reportToRevenueCardContent(report);
				break;
			case 'rentals':
				content = reportToRentalsCardContent(report);
				break;
			case 'products':
				content = reportToProductCardContent(report);
				break;
			case 'shoppers':
				content = reportToShoppersCardContent(report);
				break;
			default:
				content = 'Unknown content';
		}
		return (
			<Card className={classes.reportCard}>
				<CardContent>
					<Typography variant="subtitle1" color="textSecondary" align="center">
						{title}
					</Typography>
					{content}
				</CardContent>
			</Card>
		);
	};

	if (reportData.error) {
		return (
			<Box mt={8}>
				<Container>
					<Typography variant="h6" align="center">
						{reportData.error}
					</Typography>
				</Container>
			</Box>
		);
	}

	return (
		<Container className={classes.rootContainer}>
			<Grid container spacing={2} justifyContent={'flex-end'}>
				<Grid item className={classes.displayFlex}>
					{renderLocationSelection()}
				</Grid>
				<Grid item>{renderCurrencySelection()}</Grid>
			</Grid>
			<Grid container spacing={1} direction="row">
				<Grid item xs={12}>
					{renderMonthSelector()}
					{renderMonthDaySwitch()}
					{reportData.loading ? (
						<div className={classes.spinnerContainer}>
							<Spinner text={`${t('reports.accounting.loadingReport')}...`} />
						</div>
					) : (
						<SalesChart
							dailyReport={reportData.data?.[selectedCurrency]?.daily ?? null}
							date={currentDay}
							month={debouncedMonth}
							viewMode={viewMode}
							toggleViewMode={toggleViewMode}
							setCurrentDay={setCurrentDay}
						/>
					)}
				</Grid>
			</Grid>
			{!!reportData.data && (
				<Grid container spacing={2} sx={{ my: 2 }}>
					<Grid item xs={12} sm={12} md={5}>
						{renderMetricCard(t('products.products'), 'products', currentReportData)}
					</Grid>
					<Grid item xs={12} sm={12} md={7} className={classes.metricSubContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={12}>
								{renderMetricCard(
									t('reports.revenueMetricHeader', 'Total revenue'),
									'revenue',
									currentReportData,
								)}
							</Grid>
							<Grid item xs={12} sm={6}>
								{renderMetricCard(
									t('reports.rentalMetricHeader', 'Rentals'),
									'rentals',
									currentReportData,
								)}
							</Grid>
							<Grid item xs={12} sm={6}>
								{renderMetricCard(
									t('reports.shopperMetricHeader', 'Customers'),
									'shoppers',
									currentReportData,
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Container>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		rootContainer: {
			flex: '1 1 auto',
		},
		spinnerContainer: {
			marginTop: theme.spacing(6),
			width: '100%',
			height: 100,
			position: 'relative',
		},
		reportCard: {
			maxHeight: 300,
			overflow: 'auto',
			height: '100%',
		},
		table: {
			marginTop: '16px',
			'& td:first-child, & th:first-child': {
				paddingLeft: 0,
			},
			'& td, & th': {
				paddingRight: 0,
			},
		},
		noBorder: {
			border: 'none',
		},
		priceCell: {
			whiteSpace: 'nowrap',
		},
		monthSelector: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			margin: '0 auto',
			width: '100%',
		},
		button: {
			margin: '5px',
		},
		input: {
			display: 'none',
		},
		mediumHeight: {
			height: '32px',
		},
		lowHeight: {
			height: '24px',
		},
		dateHeader: {
			padding: '12px 0',
			minWidth: 260,
			textAlign: 'center',
			textTransform: 'capitalize',
		},
		metricSubContainer: {
			position: 'relative',
			display: 'flex',
			alignItems: 'stretch',
		},
		selectPadding: {
			paddingRight: theme.spacing(3),
		},
		select: {
			color: theme.palette.common.black,
			fontSize: '14pt',
			height: 40,
			float: 'right',
		},
		displayFlex: {
			display: 'flex',
		},
	}),
);

export default Sales;
